import { Button } from "@/common/components/button";
import { formatToNaira } from "@/common/utils/general";

export interface IRepriceRequest {
  current_price_per_base_unit: string | number | null;
  new_price_per_base_unit: string | number | null;
  selling_level?: number | null;
}

interface IRepriceConfirmProps {
  reprice: IRepriceRequest;
  /** Message from the server, so the wording lives in one place. */
  message?: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// MED-686 — the entire-product reprice confirmation, shared by both intake doors.
//
// This renders the server's 409: the backend refuses a price that differs from
// the product's current one and returns both figures, and the same request is
// accepted once `confirm_reprice` is set. Deliberately not a client-side-only
// dialog — the check that matters is the one no client can skip.
//
// The figures are per BASE UNIT because that is the canonical value the server
// compares and stores; showing them at the entered level would mean re-deriving
// a number the server did not send, and a rounding difference here would read as
// the platform disagreeing with itself.
export function RepriceConfirm({ reprice, message, loading, onConfirm, onCancel }: IRepriceConfirmProps) {
  const current = Number(reprice.current_price_per_base_unit);
  const next = Number(reprice.new_price_per_base_unit);

  return (
    <div className="mx-auto w-[30rem] p-6 sm:w-[40rem]">
      <h3 className="mb-2 font-semibold text-gray-800">This updates the price of the entire product</h3>

      <p className="mb-4 text-sm text-gray-600">
        {message ||
          "Continuing will change the price for all batches of this product and all future sales."}
      </p>

      <div className="mb-4 flex items-center gap-6 rounded border border-gray-200 p-3 text-sm">
        <div>
          <span className="block text-xs text-gray-500">Current price per base unit</span>
          <span className="font-medium text-gray-800">
            {Number.isNaN(current) ? "—" : formatToNaira(current)}
          </span>
        </div>
        <span className="text-gray-400">→</span>
        <div>
          <span className="block text-xs text-gray-500">New price per base unit</span>
          <span className="font-medium text-gray-900">
            {Number.isNaN(next) ? "—" : formatToNaira(next)}
          </span>
        </div>
      </div>

      <div className="flex justify-between gap-3">
        {/* Declining is not a second meaning of "confirm": the caller re-submits
            without a price, which the server imports against the existing one. */}
        <Button type="button" secondary onClick={onCancel} disabled={loading}>
          Keep current price
        </Button>
        <Button type="button" onClick={onConfirm} disabled={loading}>
          {loading ? "Applying…" : "Update product price"}
        </Button>
      </div>
    </div>
  );
}
